import React from "react";
import type {
  ChevronProps,
  PropsBase,
  PropsMulti,
  PropsRange,
  PropsSingle,
  ClassNames as ReactDayPickerClassNames,
  WeekProps,
} from "react-day-picker";
import { DayButton, DayPicker } from "react-day-picker";
import { RnChevronLeft, RnChevronRight } from "@remi/icons/next";
import type { Mutable } from "@remi/ts-utils";

import { useMedia } from "../hooks";
import { cn } from "../lib/utils";
import { RemiTooltip, RemiTooltipContent, RemiTooltipTrigger } from "./RemiTooltip";

// Don't use built in required functionality, doesn't allow unselecting dates
type RemiCalendarProps = PropsBase &
  (PropsRange | PropsMulti | PropsSingle) & {
    classNames?: Partial<ReactDayPickerClassNames>;
    disabledDateTooltip?: React.ReactNode;
  };

// Keep react-day-picker's own DayButton — it owns the roving-focus `.focus()` and disabled/aria-disabled
// split that keeps boundary days keyboard-reachable. A natively-disabled button drops its own pointer
// events, so hover falls through to the wrapping span (the tooltip trigger) via pointer-events-none.
const DisabledDayTooltipButton = ({
  tooltip,
  ...props
}: React.ComponentProps<typeof DayButton> & { tooltip: React.ReactNode }) => {
  if (!props.modifiers.disabled) {
    return <DayButton {...props} />;
  }
  return (
    <RemiTooltip>
      <RemiTooltipTrigger asChild>
        <span className="pointer-events-auto flex">
          <DayButton {...props} className={cn(props.className, "pointer-events-none")} />
        </span>
      </RemiTooltipTrigger>
      <RemiTooltipContent>{tooltip}</RemiTooltipContent>
    </RemiTooltip>
  );
};

// Module scope: an inline component gets a new identity each render, which remounts every week row.
const Week = (props: WeekProps) => {
  return <div {...props} />;
};

const Chevron = (props: ChevronProps) =>
  props.orientation === "left" ? (
    <RnChevronLeft size={"sm"} className="text-icon-secondary" />
  ) : (
    <RnChevronRight size={"sm"} className="text-icon-secondary" />
  );

const defaultClassNames = {
  root: "remi-calendar relative gap-0",
  month: "relative",
  months: "flex flex-row gap-9",
  month_caption: "flex h-7 items-center justify-center pointer-events-none",
  caption_label: "font-medium",
  button_previous: "absolute left-1.5 top-0 h-7 flex items-center bg-transparent p-0 opacity-50 hover:opacity-100",
  button_next: "absolute right-1.5 top-0 h-7 flex items-center bg-transparent p-0 opacity-50 hover:opacity-100",
  month_grid: "flex flex-col border-collapse mt-2",
  weekdays: "flex justify-between items-center",
  weekday: "flex items-center justify-center text-text-tertiary rounded-md h-6 w-6 font-normal",
  week: "flex justify-between mt-3 gap-2.5",
  day_button: "p-3",
  day: cn(
    "relative h-6 w-6 text-center focus-within:relative focus-within:z-20 hover:rounded-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
    "flex h-[28px] w-[28px] items-center justify-center rounded-md hover:bg-background-tertiary",
  ),
  selected:
    "!bg-control-bgSelected focus:bg-button-bgPrimaryHover !rounded-md !text-text-faint hover:!bg-control-bgSelected mx-[0px] px-[0px]",
  today: "!text-text-accent !font-semibold",
  outside: "text-text-tertiary",
  disabled: "text-text-tertiary hover:bg-transparent cursor-not-allowed",
  range_middle:
    "aria-selected:!bg-background-accentLight aria-selected:text-text-secondary hover:!bg-background-accentLight !text-text-secondary !mx-[-6px] !px-5 !rounded-xs",
  hidden: "invisible",
} satisfies Partial<ReactDayPickerClassNames>;

const isKeyOfDefaultClassNames = (key: string): key is keyof typeof defaultClassNames => {
  return key in defaultClassNames;
};

function RemiCalendar({
  className,
  classNames,
  showOutsideDays = true,
  numberOfMonths,
  components,
  disabledDateTooltip,
  ...props
}: RemiCalendarProps) {
  const { isMobile } = useMedia();

  const disabledDayButton = React.useMemo(
    () =>
      disabledDateTooltip == null
        ? undefined
        : (dayButtonProps: React.ComponentProps<typeof DayButton>) => (
            <DisabledDayTooltipButton {...dayButtonProps} tooltip={disabledDateTooltip} />
          ),
    [disabledDateTooltip],
  );

  const combinedClassNames = Object.entries(defaultClassNames).reduce<Mutable<Partial<ReactDayPickerClassNames>>>(
    (acc, [key, value]) => {
      if (isKeyOfDefaultClassNames(key)) {
        acc[key] = cn(value, classNames?.[key]);
      }
      return acc;
    },
    {},
  );

  return (
    <DayPicker
      numberOfMonths={isMobile ? 1 : (numberOfMonths ?? (props.mode === "range" ? 2 : 1))}
      showOutsideDays={showOutsideDays}
      className={cn("select-none text-[13px]", className)}
      classNames={{
        ...classNames,
        ...combinedClassNames,
      }}
      components={{
        Week,
        Chevron,
        ...(disabledDayButton && { DayButton: disabledDayButton }),
        ...components,
      }}
      {...props}
      // Must stay after the spread. react-day-picker only nests Nav inside each Month for "around";
      // a falsy navLayout makes Nav a third flex child of `months` (+36px, which the deleted -mr-9
      // used to cancel) and "after" collapses both chevrons into the last month. Measured, DES-2731.
      navLayout="around"
    />
  );
}
RemiCalendar.displayName = "RemiCalendar";

export { RemiCalendar, type RemiCalendarProps };
